// Page-scale filters for the revenue screen.
//
// PAGE-SCALE means every panel re-cuts together — hero, metric strip, verdict
// buckets, both tables and the daily series. A filter that only narrowed one
// table would put a filtered number beside an unfiltered one on the same
// screen, which is worse than no filter: the reader has no way to tell which
// is which.
//
// The filtering itself happens on the SERVER, not here. Every rate on this
// screen is recomputed from summed numerators and denominators, and folding a
// filtered subset in the browser would be a second implementation of that
// arithmetic. This component only decides WHAT to ask for.
//
// State lives in the URL. A filtered view of the company's revenue is exactly
// the thing someone pastes into a message, and query params make that free —
// they also survive a refresh, which local state does not.

import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown } from 'lucide-react';
import s from './Filters.module.css';

function useOutsideClose(ref, onClose) {
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [ref, onClose]);
}

// A multi-select that shows what is selected without making you open it — the
// count on the chip is the difference between "is a filter on?" being a glance
// and being a click.
function MultiSelect({ label, options, selected, onChange, renderOption }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useOutsideClose(ref, () => setOpen(false));

  const toggle = (v) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange([...next]);
  };

  const needle = q.trim().toLowerCase();
  const shown = needle
    ? options.filter((o) => String(o.label).toLowerCase().includes(needle))
    : options;

  return (
    <div className={s.control} ref={ref}>
      <button type="button"
              className={`${s.trigger} ${selected.length ? s.triggerOn : ''}`}
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}>
        <span className={s.triggerLabel}>{label}</span>
        {selected.length > 0 && <span className={s.count}>{selected.length}</span>}
        <ChevronDown size={13} aria-hidden="true" />
      </button>

      {open && (
        <div className={s.menu} role="listbox">
          {options.length > 8 && (
            <input className={s.search} value={q} autoFocus
                   onChange={(e) => setQ(e.target.value)}
                   placeholder={`Filter ${label.toLowerCase()}…`}
                   aria-label={`Filter ${label}`} />
          )}
          <div className={s.options}>
            {shown.map((o) => (
              <label key={o.value} className={s.option}>
                <input type="checkbox" checked={selected.includes(o.value)}
                       onChange={() => toggle(o.value)} />
                <span className={s.optionBody}>
                  {renderOption ? renderOption(o) : o.label}
                </span>
              </label>
            ))}
            {!shown.length && <div className={s.none}>nothing matches</div>}
          </div>
          {selected.length > 0 && (
            <button type="button" className={s.clearInline}
                    onClick={() => { onChange([]); setQ(''); }}>
              Clear {label.toLowerCase()}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// The default vocabulary is the advertiser triple this component was born
// with. It is a DEFAULT, not a hard-coding: a caller cutting a different
// dataset (the screens pivot cuts by media owner and screen) passes its own
// `fields` descriptors and everything below — triggers, counts, chips,
// clear-all — renders them instead. Descriptor shape:
//   { key,          // property in `value`, and the URL param name
//     label,        // trigger label
//     chipNoun,     // lowercase noun on the chip ("advertiser: …")
//     options,      // [{ value, label, meta? }]
//     renderOption, // optional custom option row
//     chipText }    // optional value → chip label (default: option lookup)
const defaultFields = (facets) => [
  { key: 'advertisers', label: 'Advertiser', chipNoun: 'advertiser',
    options: (facets.advertisers || []).map((a) => ({ value: a, label: a })) },
  { key: 'statuses', label: 'Status', chipNoun: 'status',
    options: (facets.statuses || []).map((a) => ({ value: a, label: a })) },
  { key: 'campaigns', label: 'Campaign', chipNoun: 'campaign',
    options: (facets.campaigns || []).map((c) => ({ value: c.id, label: c.name, meta: c })),
    renderOption: (o) => (
      <>
        <span className={s.optName}>{o.label}</span>
        <span className={s.optMeta}>{o.meta.advertiser} · {o.meta.status}</span>
      </>
    ),
    chipText: (id) => (facets.campaigns || []).find((c) => c.id === id)?.name || id.slice(0, 8) },
];

// `compact` is the pivot screens' variant: the same controls, inline in
// an existing row, with no boxed container, no "FILTER" label and no chip
// rail. It exists because those pages must fit their chrome in two short rows —
// the selected COUNT already rides on each trigger, so the chip rail was
// restating what the control beside it says, at the cost of a whole row.
export default function Filters({ facets, value, onChange, summary, compact, fields }) {
  const defs = fields || defaultFields(facets);
  const set = (k, v) => onChange({ ...value, [k]: v });
  const activeCount = defs.reduce((t, f) => t + (value[f.key]?.length || 0), 0);
  const chipText = (f, v) => (f.chipText
    ? f.chipText(v)
    : (f.options.find((o) => o.value === v)?.label ?? String(v)));

  return (
    <div className={compact ? s.wrapCompact : s.wrap}>
      <div className={s.row}>
        {!compact && <span className={s.title}>Filter</span>}

        {defs.map((f) => (
          <MultiSelect
            key={f.key}
            label={f.label}
            options={f.options}
            selected={value[f.key] || []}
            onChange={(v) => set(f.key, v)}
            renderOption={f.renderOption}
          />
        ))}

        {activeCount > 0 && (
          <button type="button" className={s.clearAll}
                  onClick={() => onChange(Object.fromEntries(defs.map((f) => [f.key, []])))}>
            Clear all
          </button>
        )}

        {/* HOW MUCH OF THE BUSINESS IS BEING HIDDEN. A filtered revenue screen
            that does not say what it excluded invites someone to read a slice
            as the whole — which on this page means reading a fraction of the
            company's revenue as the company's revenue. */}
        {summary?.active && (
          <span className={s.scope}>
            showing <b>{summary.share}</b> of the window&rsquo;s{' '}
            {summary.totalLabel} accrued
          </span>
        )}
      </div>

      {activeCount > 0 && !compact && (
        <div className={s.chips}>
          {defs.flatMap((f) => (value[f.key] || []).map((v) => (
            <Chip key={`${f.key}-${v}`}
                  onRemove={() => set(f.key, value[f.key].filter((x) => x !== v))}>
              {f.chipNoun || f.label.toLowerCase()}: {chipText(f, v)}
            </Chip>
          )))}
        </div>
      )}
    </div>
  );
}

function Chip({ children, onRemove }) {
  return (
    <span className={s.chip}>
      {children}
      <button type="button" className={s.chipX} onClick={onRemove} aria-label="Remove filter">
        <X size={11} />
      </button>
    </span>
  );
}
